import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { AppBuilderProjectRequest, AppBuilderProjectResponse, AppBuilderApiConfig, AppBuilderApiError } from '../types/appbuilder';
import { API_URL } from '../config';

class AppBuilderApiService {
  private api: AxiosInstance;
  private baseUrl: string;
  private superpossibleKey: string;

  constructor() {
    // Use the same API configuration as the main app
    this.baseUrl = API_URL;
    this.superpossibleKey = localStorage.getItem('superpossible_key') || '';
    this.api = axios.create({
      baseURL: `${this.baseUrl}/api/appbuilder`,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        console.log(`AppBuilder API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error: any) => {
        console.error('AppBuilder Request error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        console.log(`AppBuilder API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      (error: AxiosError) => {
        console.error('AppBuilder Response error:', error);
        const apiError: AppBuilderApiError = {
          error: 'AppBuilder API Error',
          message: error.message,
        };

        if (error.response?.data) {
          Object.assign(apiError, error.response.data);
        }

        return Promise.reject(apiError);
      }
    );
  }

  // Project Management
  async createProject(request: AppBuilderProjectRequest): Promise<AppBuilderProjectResponse> {
    try {
      // Add superpossible_key to the request
      const requestWithKey = {
        ...request,
        superpossible_key: this.superpossibleKey
      };
      const response = await this.api.post<AppBuilderProjectResponse>('/projects', requestWithKey);
      return response.data;
    } catch (error) {
      console.error('Failed to create AppBuilder project:', error);
      throw error;
    }
  }

  async getProject(projectId: string): Promise<AppBuilderProjectResponse> {
    try {
      const response = await this.api.get<AppBuilderProjectResponse>(`/projects/${projectId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get AppBuilder project:', error);
      throw error;
    }
  }

  async getAllProjects(): Promise<AppBuilderProjectResponse[]> {
    try {
      const response = await this.api.get<AppBuilderProjectResponse[]>('/projects');
      return response.data;
    } catch (error) {
      console.error('Failed to get all AppBuilder projects:', error);
      throw error;
    }
  }

  async deleteProject(projectId: string): Promise<void> {
    try {
      await this.api.delete(`/projects/${projectId}`);
    } catch (error) {
      console.error('Failed to delete AppBuilder project:', error);
      throw error;
    }
  }

  // Health Check
  async healthCheck(): Promise<{ status: string; message: string }> {
    try {
      const response = await this.api.get('/health');
      return response.data;
    } catch (error) {
      console.error('AppBuilder Health check failed:', error);
      throw error;
    }
  }

  // WebSocket URL
  getWebSocketUrl(projectId: string): string {
    const isDevelopment = window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1';
    
    if (isDevelopment) {
      return `ws://localhost:8000/api/appbuilder/ws/${projectId}/${this.superpossibleKey}`;
    } else {
      // Production: use same protocol and hostname as current page
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const hostname = window.location.hostname; // No port!
      return `${protocol}//${hostname}/api/appbuilder/ws/${projectId}/${this.superpossibleKey}`;
    }
  }
}

export const appBuilderApiService = new AppBuilderApiService();
export default appBuilderApiService;
